'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { cartService, CartItem } from '@/services/cartService';
import { useAuth } from './AuthContext';

interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  loading: boolean;
  addToCart: (deviceId: string, quantity?: number) => Promise<void>;
  removeFromCart: (cartItemId: string) => Promise<void>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  getCartTotal: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const refreshCart = async () => {
    if (!user) {
      setCartItems([]);
      return;
    }

    try {
      setLoading(true);
      const items = await cartService.getCartItems();
      setCartItems(items);
    } catch (error) {
      // Error handled in service
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCart();
  }, [user]);

  const addToCart = async (deviceId: string, quantity: number = 1) => {
    try {
      await cartService.addToCart(deviceId, quantity);
      await refreshCart();
    } catch (error) {
      throw error;
    }
  };

  const removeFromCart = async (cartItemId: string) => {
    try {
      await cartService.removeFromCart(cartItemId);
      await refreshCart();
    } catch (error) {
      throw error;
    }
  };

  const updateQuantity = async (cartItemId: string, quantity: number) => {
    try {
      await cartService.updateQuantity(cartItemId, quantity);
      await refreshCart();
    } catch (error) {
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await cartService.clearCart();
      await refreshCart();
    } catch (error) {
      throw error;
    }
  };

  const getCartTotal = () => {
    return cartService.calculateTotal(cartItems);
  };

  const cartCount = cartService.getItemCount(cartItems);

  return (
    <CartContext.Provider data-component-id="src/contexts/CartContext.tsx:91:4" data-component-path="src/contexts/CartContext.tsx" data-component-line="91" data-component-end-line="105" data-component-file="CartContext.tsx" data-component-name="CartContext.Provider" data-component-content="%7B%22elementName%22%3A%22CartContext.Provider%22%7D"
      value={{
        cartItems,
        cartCount,
        loading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        refreshCart,
        getCartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}